/**
 * NGU NGHIA CUA LENH CHINH SUA: dao nguoc, gop, va mo ta cho nguoi dung.
 *
 * Nguyen tac: khong bao gio luu "trang thai truoc / trang thai sau" dang nhi
 * phan. Luu Y DINH. Mot lenh dao nguoc duoc la mot lenh khac cung kieu du lieu,
 * tuan tu hoa duoc, va gui qua mang duoc. Dieu do cho ta undo/redo, cong tac
 * thoi gian thuc, va lich su phien ban tu CUNG MOT co che.
 */
import type { Annotation, EditCommand } from '@pdfeditor/types';
import type { PagePlan } from '../core/pages/plan';

/** Trang thai toi thieu can de tinh lenh dao nguoc. */
export interface InversionContext {
  annotationsById: ReadonlyMap<string, Annotation>;
  /**
   * So do trang TAI THOI DIEM AP DUNG lenh. Can de dao nguoc 'page.remove':
   * hop dong kieu bat lenh chen phai noi ro no chen trang NAO, va thong tin do
   * chi con o so do ngay truoc luc xoa.
   */
  pagePlan?: PagePlan;
}

/**
 * Tinh lenh dao nguoc TAI THOI DIEM AP DUNG (khong phai luc undo) - dieu nay
 * quan trong: trang thai truoc do co the da bien mat khi nguoi dung bam undo.
 *
 * Tra ve null nghia la lenh KHONG dao nguoc duoc. Do khong phai thieu sot ma
 * la thuoc tinh that cua thao tac (vd che thong tin la mot chieu theo thiet ke).
 */
export function invert(command: EditCommand, ctx: InversionContext): EditCommand | null {
  switch (command.kind) {
    case 'text.replace':
      // `align` DI THEO ca lenh nghich, va day la mot lua chon co danh doi.
      //
      // Dat chu cu ve cho bang can le nguoi dung vua chon KHONG hoan toan dung
      // y nghia "tra lai nhu cu": can le goc cua chu cu khong nam trong lenh, no
      // chi suy ra duoc tu chinh trang giay. Nhung bo truong nay di thi
      // invert(invert(c)) khong con bang c, tuc la mot vong gui qua mang hay mot
      // lan redo se AM THAM lam mat lua chon cua nguoi dung. Mat thong tin la
      // hong nang hon; giu lai thi ben nhan van con du du lieu de xu ly khac.
      //
      // Duong hoan tac THAT trong ung dung khong dua vao ham nay: kho trang thai
      // cat bot danh sach lenh (xem state/store.ts), nen Ctrl+Z that su tra ve
      // dung trang thai truoc do, ke ca can le.
      //
      // CHE CHU thi lenh nghich la mot lenh SUA THAT: dat lai chu cu vao cho.
      // Do khong phai "bo mang che di" - mang che da nam trong tep - ma la ve
      // lai chu len tren no. Ket qua nhin giong ban dau; hai lop van con.
      // Duong hoan tac THAT khong di qua day (kho trang thai cat bot danh sach
      // lenh), nhung mot ben cong tac nhan lenh nay phai lam duoc dieu do.
      return {
        kind: 'text.replace',
        blockId: command.blockId,
        newText: command.previousText,
        previousText: command.newText,
        ...(command.align ? { align: command.align } : {}),
        ...(command.bold ? { bold: command.bold } : {}),
        ...(command.italic ? { italic: command.italic } : {}),
        // Ho font di theo cung ly do voi `align`: bo no di thi invert(invert(c))
        // khong con bang c, va mot vong redo / gui qua mang se AM THAM tra lai chu
        // cu bang phong mac dinh thay vi phong nguoi dung da chon.
        ...(command.fontFamily ? { fontFamily: command.fontFamily } : {}),
        // Co chu cung di theo, cung ly do: mot vong redo / gui qua mang khong duoc
        // AM THAM doi co chu ma nguoi dung da chon.
        ...(command.fontSizePt !== undefined ? { fontSizePt: command.fontSizePt } : {}),
      };

    case 'text.style': {
      // Hop dong khong mang theo gia tri cu, nen ta dung chinh trang thai hien
      // tai de dung lenh nghich. Khi chua co gia tri cu (chua tung doi kieu),
      // lenh nghich la lenh rong - vo hai.
      return {
        kind: 'text.style',
        runIds: [...command.runIds],
      };
    }

    case 'text.insert':
      // Chua co lenh 'text.remove' trong hop dong. Van ban vua chen thuoc ve
      // mot khoi moi, nen dao nguoc = thay noi dung khoi do bang rong.
      return {
        kind: 'text.replace',
        blockId: `inserted@${command.pageIndex}:${command.at.x},${command.at.y}`,
        newText: '',
        previousText: command.text,
      };

    case 'annotation.add':
      return { kind: 'annotation.remove', id: command.annotation.id };

    case 'annotation.remove': {
      const previous = ctx.annotationsById.get(command.id);
      if (!previous) return null;
      return { kind: 'annotation.add', annotation: { ...previous } };
    }

    case 'annotation.update': {
      const previous = ctx.annotationsById.get(command.id);
      if (!previous) return null;
      // Chi dao nguoc dung nhung truong bi dong vao, khong khoi phuc ca doi tuong.
      const patch: Partial<Annotation> = {};
      for (const key of Object.keys(command.patch) as (keyof Annotation)[]) {
        (patch as Record<string, unknown>)[key] = previous[key];
      }
      return { kind: 'annotation.update', id: command.id, patch };
    }

    case 'page.insert':
      // Chen mot o o `atIndex` thi o do NAM DUNG o `atIndex`; xoa chinh no la
      // dao nguoc chinh xac, khong phu thuoc trang thai nao khac.
      return { kind: 'page.remove', pageIndex: command.atIndex };

    case 'page.remove': {
      // Truoc day cho nay tra ve null, tuc la "xóa trang không hoàn tác được" -
      // sai, va la kieu sai te nhat: nguoi dung xoa nham mot trang roi bam
      // Ctrl+Z va khong co gi xay ra.
      //
      // Trang bi xoa khong bao gio bi mat: byte goc bat bien (ADR-003), nen mot
      // o trang chi la mot cap (ma tai lieu nguon, chi so trang nguon). Dao
      // nguoc = chen lai DUNG cap do vao DUNG cho cu.
      //
      // GIOI HAN CO Y, phai noi ro: lenh chen theo hop dong kieu khong mang
      // theo do xoay. Neu trang bi xoa da tung duoc xoay, rieng lenh nghich nay
      // khong khoi phuc do xoay do. Duong hoan tac THAT trong ung dung khong
      // dua vao no: kho trang thai dung lai so do tu TIEN TO danh sach lenh
      // (replayPagePlan), nen bo lenh 'page.remove' di la moi lenh xoay truoc do
      // van con nguyen hieu luc. Lenh nghich nay ton tai cho viec tuan tu hoa va
      // cho cac ben ngang hang (cong tac), khong phai cho undo cuc bo.
      const slot = ctx.pagePlan?.[command.pageIndex];
      if (!slot) return null;
      if (slot.sourcePageIndex === null) {
        return { kind: 'page.insert', atIndex: command.pageIndex, source: { kind: 'blank' } };
      }
      return {
        kind: 'page.insert',
        atIndex: command.pageIndex,
        source: { kind: 'document', docId: slot.docId, pageIndex: slot.sourcePageIndex },
      };
    }

    case 'page.reorder':
      // DUNG voi - va CHI dung voi - ngu nghia "go o `from` ra roi chen vao vi
      // tri `to` cua mang DA NGAN DI" (xem core/pages/plan.ts). Voi ngu nghia
      // "chen truoc phan tu dang o `to`", lenh nghich nay lech mot o khi keo
      // trang xuong phia duoi. pageOps.test.ts chot lai dieu do.
      return { kind: 'page.reorder', from: command.to, to: command.from };

    case 'page.rotate': {
      const back = ((360 - command.delta) % 360) as 90 | 180 | 270;
      return { kind: 'page.rotate', pageIndex: command.pageIndex, delta: back };
    }

    case 'image.insert':
      // Hop dong chua co lenh xoa doi tuong anh.
      return null;

    case 'content.image.transform':
      // Ma tran cu di theo lenh, nen phep dao nguoc la chinh xac tuyet doi -
      // khong phu thuoc trang thai nao khac, khong phai doc lai luong noi dung.
      return {
        kind: 'content.image.transform',
        pageIndex: command.pageIndex,
        occurrence: command.occurrence,
        matrix: command.previousMatrix,
        previousMatrix: command.matrix,
      };

    case 'content.image.remove':
      // KHONG dao nguoc duoc bang mot lenh: hop dong khong co lenh "chen mot
      // luot ve anh vao content stream". Day la mot thuoc tinh that cua hop
      // dong, khong phai thieu sot o day.
      //
      // Ctrl+Z trong ung dung VAN hoat dong: kho trang thai cat bot danh sach
      // lenh va duong ghi phat lai tu byte GOC BAT BIEN (ADR-003), nen bo lenh
      // nay ra la anh tu tro lai nguyen ven.
      return null;

    case 'redact.apply':
      // MOT CHIEU THEO THIET KE. Che thong tin xoa han glyph, font subset mo coi
      // va sieu du lieu; neu dao nguoc duoc thi no da khong phai la che thong tin.
      return null;

    default:
      return null;
  }
}

/** Nhan tieng Viet cho muc lich su. */
export function describe(command: EditCommand): string {
  switch (command.kind) {
    case 'text.replace':
      // "Che" chu khong phai "Xoa": muc lich su cung la mot cho nguoi dung doc,
      // va goi no la xoa o day thi loi canh bao o o soan tro thanh vo nghia.
      return command.cover
        ? `Che chữ: “${truncate(command.previousText)}”`
        : `Sửa văn bản: “${truncate(command.newText)}”`;
    case 'text.style':
      return `Đổi kiểu chữ (${command.runIds.length} đoạn)`;
    case 'text.insert':
      return `Chèn văn bản ở trang ${command.pageIndex + 1}`;
    case 'annotation.add':
      return `Thêm chú thích ${ANNOTATION_LABEL[command.annotation.kind] ?? command.annotation.kind}`;
    case 'annotation.update':
      return 'Sửa chú thích';
    case 'annotation.remove':
      return 'Xóa chú thích';
    case 'page.insert':
      return command.source.kind === 'blank'
        ? `Chèn trang trắng ở vị trí ${command.atIndex + 1}`
        : `Chèn trang ${command.source.pageIndex + 1} từ tệp khác ở vị trí ${command.atIndex + 1}`;
    case 'page.remove':
      return `Xóa trang ${command.pageIndex + 1}`;
    case 'page.reorder':
      return `Chuyển trang ${command.from + 1} → ${command.to + 1}`;
    case 'page.rotate':
      return `Xoay trang ${command.pageIndex + 1} thêm ${command.delta}°`;
    case 'image.insert':
      return `Chèn ảnh vào trang ${command.pageIndex + 1}`;
    case 'content.image.transform':
      return `Di chuyển ảnh có sẵn ở trang ${command.pageIndex + 1}`;
    case 'content.image.remove':
      return `Xóa ảnh có sẵn ở trang ${command.pageIndex + 1}`;
    case 'redact.apply':
      return `Che thông tin (${command.areas.length} vùng)`;
    default:
      return 'Thao tác';
  }
}

const ANNOTATION_LABEL: Partial<Record<string, string>> = {
  highlight: 'tô sáng',
  underline: 'gạch chân',
  strikeout: 'gạch ngang',
  squiggly: 'gạch lượn sóng',
  ink: 'vẽ tay',
  square: 'khung chữ nhật',
  circle: 'khung tròn',
  line: 'đường thẳng',
  arrow: 'mũi tên',
  'text-note': 'ghi chú',
  'free-text': 'chữ tự do',
  stamp: 'con dấu',
};

function truncate(s: string, max = 28): string {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
}

/** Cua so thoi gian de gop hai lenh lien tiep thanh mot muc undo. */
export const COALESCE_WINDOW_MS = 900;

/**
 * Thu gop lenh moi vao lenh truoc do. Tra ve lenh da gop, hoac null neu khong
 * gop duoc. Gop la ly do Ctrl+Z khong lui tung ky tu mot khi go van ban.
 */
export function coalesce(
  previous: EditCommand,
  next: EditCommand,
  elapsedMs: number,
): EditCommand | null {
  if (elapsedMs > COALESCE_WINDOW_MS) return null;

  if (previous.kind === 'text.replace' && next.kind === 'text.replace') {
    if (previous.blockId !== next.blockId) return null;
    // Giu previousText CU NHAT: undo phai quay ve truoc ca chuoi go.
    // Can le thi nguoc lai - lay lua chon MOI NHAT, y nhu cach 'text.style' gop
    // co chu va mau. Doi can le roi go them chu trong cung mot nhip phai ra mot
    // muc lich su duy nhat mang can le vua chon.
    // MOT lan CHE va mot lan SUA khong duoc gop: chung la hai y dinh khac nhau
    // tren cung mot pham vi, va gop chung se lam mot buoc Ctrl+Z nhay qua ca
    // hai. Nguoi dung che nham roi bam hoan tac phai thay chu cu tro lai, chu
    // khong phai nhay ve truoc ca lan sua truoc do.
    if (previous.cover !== next.cover) return null;
    const align = next.align ?? previous.align;
    const bold = next.bold ?? previous.bold;
    const italic = next.italic ?? previous.italic;
    // Ho font: lay lua chon MOI NHAT, y nhu can le. Doi phong roi go them chu
    // trong cung mot nhip phai ra mot muc lich su duy nhat mang phong vua chon.
    const fontFamily = next.fontFamily ?? previous.fontFamily;
    // Co chu: cung lay lua chon MOI NHAT, cung ly do voi phong va can le.
    const fontSizePt = next.fontSizePt ?? previous.fontSizePt;
    return {
      kind: 'text.replace',
      blockId: previous.blockId,
      newText: next.newText,
      previousText: previous.previousText,
      ...(align ? { align } : {}),
      ...(bold !== undefined ? { bold } : {}),
      ...(italic !== undefined ? { italic } : {}),
      ...(fontFamily ? { fontFamily } : {}),
      ...(fontSizePt !== undefined ? { fontSizePt } : {}),
      ...(next.cover ? { cover: true } : {}),
    };
  }

  // MOT CU KEO ANH = MOT MUC HOAN TAC.
  //
  // Khong gop thi moi khung hinh cua cu keo la mot muc lich su rieng, va Ctrl+Z
  // se nhich anh lui tung diem mot - hang chuc lan bam de tra ve cho cu. Giu
  // `previousMatrix` CU NHAT dung ly do voi previousText o tren: hoan tac phai
  // quay ve truoc CA cu keo.
  if (previous.kind === 'content.image.transform' && next.kind === 'content.image.transform') {
    if (previous.pageIndex !== next.pageIndex) return null;
    if (previous.occurrence !== next.occurrence) return null;
    return {
      kind: 'content.image.transform',
      pageIndex: next.pageIndex,
      occurrence: next.occurrence,
      matrix: next.matrix,
      previousMatrix: previous.previousMatrix,
    };
  }

  if (previous.kind === 'text.style' && next.kind === 'text.style') {
    if (!sameIds(previous.runIds, next.runIds)) return null;
    const merged: Extract<EditCommand, { kind: 'text.style' }> = {
      kind: 'text.style',
      runIds: [...next.runIds],
    };
    const size = next.fontSizePt ?? previous.fontSizePt;
    const color = next.colorRgb ?? previous.colorRgb;
    if (size !== undefined) merged.fontSizePt = size;
    if (color !== undefined) merged.colorRgb = color;
    return merged;
  }

  if (previous.kind === 'annotation.update' && next.kind === 'annotation.update') {
    if (previous.id !== next.id) return null;
    return {
      kind: 'annotation.update',
      id: next.id,
      patch: { ...previous.patch, ...next.patch },
    };
  }

  return null;
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((id) => setA.has(id));
}
